/* oxlint-disable lingui/no-unlocalized-strings -- TODO(i18n): traducir (deuda) */
/**
 * Default command catalogue and key map for Habits.
 *
 * Commands are declared without `run` handlers — features bind their own
 * runtime behaviour through `useCommandHandler`. The keymap is the single
 * source of truth that the help modal and profile/shortcuts page consume.
 *
 * Title and description strings use plain English here so the Lingui
 * extractor can pick them up directly via `t()` at render time in the UI.
 */

import type { Command, KeyBinding } from "@fleet/shortcuts";

export const COMMAND_IDS = {
  HELP_OPEN: "help.open",
  HELP_CLOSE: "help.close",

  HABIT_NEW: "habit.create",

  UNDO: "app.undo",

  UI_TOGGLE_SIDEBAR: "ui.toggle-sidebar",
  UI_TOGGLE_BLUR: "ui.toggle-blur",
  UI_TOGGLE_FOLLOW_DEVICE_THEME: "ui.toggle-follow-device-theme",
  UI_TOGGLE_BACKGROUND_STYLE: "ui.toggle-background-style",

  GOTO_TODAY: "nav.goto-today",
  GOTO_HABITS: "nav.goto-habits",
  GOTO_CALENDAR: "nav.goto-calendar",
  GOTO_STATS: "nav.goto-stats",
  GOTO_PROFILE: "nav.goto-profile",
  GOTO_THEMES: "nav.goto-themes",
  GOTO_SHORTCUTS: "nav.goto-shortcuts",
  GOTO_LANGUAGE: "nav.goto-language",

  DIALOG_CONFIRM: "dialog.confirm",
  DIALOG_CLOSE: "dialog.close",
} as const;

const GLOBAL_WHEN = "!input.focused && !dialog.open";

export const defaultCommands: readonly Command[] = [
  // ── Help ────────────────────────────────────────────────────────────────
  {
    id: COMMAND_IDS.HELP_OPEN,
    category: "Global Actions",
    titleKey: "Open keyboard shortcuts help",
    when: GLOBAL_WHEN,
  },
  {
    id: COMMAND_IDS.HELP_CLOSE,
    category: "Global Actions",
    titleKey: "Close keyboard shortcuts help",
  },

  // ── Habits ──────────────────────────────────────────────────────────────
  {
    id: COMMAND_IDS.HABIT_NEW,
    category: "Global Actions",
    titleKey: "Create new habit",
    when: GLOBAL_WHEN,
  },

  // ── Undo (L1) ───────────────────────────────────────────────────────────
  // Outside text inputs (there Ctrl+Z belongs to field itself) and dialogs
  // (undoing mutations "blindly" under a modal is confusing).
  {
    id: COMMAND_IDS.UNDO,
    category: "Global Actions",
    titleKey: "Undo last action",
    when: GLOBAL_WHEN,
  },

  // ── UI toggles ──────────────────────────────────────────────────────────
  {
    id: COMMAND_IDS.UI_TOGGLE_SIDEBAR,
    category: "Global Actions",
    titleKey: "Toggle sidebar",
    when: GLOBAL_WHEN,
  },
  {
    id: COMMAND_IDS.UI_TOGGLE_BLUR,
    category: "Global Actions",
    titleKey: "Toggle blur effect",
    when: GLOBAL_WHEN,
  },
  {
    id: COMMAND_IDS.UI_TOGGLE_FOLLOW_DEVICE_THEME,
    category: "Global Actions",
    titleKey: "Toggle follow-device theme",
    when: GLOBAL_WHEN,
  },
  {
    id: COMMAND_IDS.UI_TOGGLE_BACKGROUND_STYLE,
    category: "Global Actions",
    titleKey: "Toggle background style (gradients / lines)",
  },

  // ── Navigation ──────────────────────────────────────────────────────────
  {
    id: COMMAND_IDS.GOTO_TODAY,
    category: "Navigation",
    titleKey: "Go to Today",
    when: GLOBAL_WHEN,
  },
  {
    id: COMMAND_IDS.GOTO_HABITS,
    category: "Navigation",
    titleKey: "Go to Habits",
    when: GLOBAL_WHEN,
  },
  {
    id: COMMAND_IDS.GOTO_CALENDAR,
    category: "Navigation",
    titleKey: "Go to Calendar",
    when: GLOBAL_WHEN,
  },
  {
    id: COMMAND_IDS.GOTO_STATS,
    category: "Navigation",
    titleKey: "Go to Statistics",
    when: GLOBAL_WHEN,
  },
  {
    id: COMMAND_IDS.GOTO_PROFILE,
    category: "Navigation",
    titleKey: "Go to Profile",
    when: GLOBAL_WHEN,
  },
  {
    id: COMMAND_IDS.GOTO_THEMES,
    category: "Navigation",
    titleKey: "Go to Themes",
    when: GLOBAL_WHEN,
  },
  {
    id: COMMAND_IDS.GOTO_SHORTCUTS,
    category: "Navigation",
    titleKey: "Go to Shortcuts settings",
    when: GLOBAL_WHEN,
  },
  {
    id: COMMAND_IDS.GOTO_LANGUAGE,
    category: "Navigation",
    titleKey: "Go to Language settings",
    when: GLOBAL_WHEN,
  },

  // ── Dialogs ─────────────────────────────────────────────────────────────
  {
    id: COMMAND_IDS.DIALOG_CONFIRM,
    category: "Dialogs",
    titleKey: "Confirm dialog action",
    when: "dialog.open",
  },
  {
    id: COMMAND_IDS.DIALOG_CLOSE,
    category: "Dialogs",
    titleKey: "Close dialog",
  },
];

export const defaultKeymap: readonly KeyBinding[] = [
  // ── Help ────────────────────────────────────────────────────────────────
  { command: COMMAND_IDS.HELP_OPEN, keys: "?" },

  // ── Habits ──────────────────────────────────────────────────────────────
  { command: COMMAND_IDS.HABIT_NEW, keys: "n" },

  // ── Undo (Mod = Ctrl en Win/Linux, Cmd en macOS) ────────────────────────
  { command: COMMAND_IDS.UNDO, keys: "Mod+z" },

  // ── UI toggles ──────────────────────────────────────────────────────────
  { command: COMMAND_IDS.UI_TOGGLE_SIDEBAR, keys: "Mod+b" },
  { command: COMMAND_IDS.UI_TOGGLE_BLUR, keys: "Mod+Shift+b" },
  { command: COMMAND_IDS.UI_TOGGLE_FOLLOW_DEVICE_THEME, keys: "Mod+Shift+d" },
  // UI_TOGGLE_BACKGROUND_STYLE intentionally has no default binding.

  // ── Navigation chords ──────────────────────────────────────────────────
  { command: COMMAND_IDS.GOTO_TODAY, keys: "g h" },
  { command: COMMAND_IDS.GOTO_TODAY, keys: "g t" },
  { command: COMMAND_IDS.GOTO_HABITS, keys: "g a" },
  { command: COMMAND_IDS.GOTO_CALENDAR, keys: "g c" },
  { command: COMMAND_IDS.GOTO_STATS, keys: "g x" },
  { command: COMMAND_IDS.GOTO_PROFILE, keys: "g s" },
  { command: COMMAND_IDS.GOTO_THEMES, keys: "g e" },
  { command: COMMAND_IDS.GOTO_SHORTCUTS, keys: "g k" },
  { command: COMMAND_IDS.GOTO_LANGUAGE, keys: "g l" },

  // ── Dialogs ─────────────────────────────────────────────────────────────
  // NOTE: Dialog Escape is intentionally NOT bound globally. Kobalte's Dialog
  // primitive owns its own Escape behaviour (with focus trap restoration);
  // custom overlays bind locally via useBinding() while they're open.
  { command: COMMAND_IDS.DIALOG_CONFIRM, keys: "Enter", scope: "dialog" },
];
